import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, tap } from 'rxjs';
import { BindPolicyResultModel } from 'src/app/models/bind-policy-result.model';
import { QQQuestionnaireViewModel } from 'src/app/models/qq-questionnaire.view-model';
import { QuoteModel } from 'src/app/models/quote.model';
import { logError } from 'src/app/sentry';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { ApiService } from 'src/app/services/api.service';
import { parseQuery } from 'src/app/util/parse-query';

@Component({
  selector: 'app-apg-full-quote',
  templateUrl: './apg-full-quote.component.html',
  styleUrls: ['./apg-full-quote.component.scss'],
})
export class ApgFullQuoteComponent implements OnInit {

  @Input() key: string;
  @Input() quickQuote: QQQuestionnaireViewModel;
  @Input() quote: QuoteModel;

  @Output() back = new EventEmitter();
  @Output() newQuote = new EventEmitter();

  loading = false;
  error: string = null;
  pageIndex$ = new BehaviorSubject(0);
  pages = [
    'load',
    'summary-1',
    'address',
    'summary-2',
    'declaration',
    'questions',
    'result',
    'final-declaration',
    'bank-details',
    'complete',
  ];

  get page() {
    return this.pages[this.pageIndex$.value];
  }

  magnumResult: any;

  constructor(
    private api: ApiService,
    private analyticsService: AnalyticsService
  ) { }

  ngOnInit() {
    this.pageIndex$.pipe(
      tap(x => this.analyticsService.logPageView(`full_quote/${this.page}`, { page_title: `Full Quote | ${this.page}` }))
    ).subscribe();

    if (this.quote)
      this.pageIndex$.next(2);

    const query = parseQuery(location.search);
    if (query['debugPage'])
      this.pageIndex$.next(parseInt(query['debugPage']));
  }

  nextPage() {
    if (['result', 'final-declaration', 'bank-details', 'complete'].includes(this.page) && !this.checkAccess()) {
      this.pageIndex$.next(6);
      return;
    }
    this.pageIndex$.next(this.pageIndex$.value + 1);
  }

  prevPage() {
    if (['load', 'summary-1', 'address'].includes(this.page))
      this.back.emit();
    else
      this.pageIndex$.next(this.pageIndex$.value - 1);
  }

  quoteLoaded(quote: QuoteModel) {
    const q = this.quickQuote;
    for (const answer of quote.answers)
      q.saveAnswer(answer.key, answer.value);

    this.quote = quote;
    if (!this.key)
      this.key = this.quote.key;
    this.nextPage();
  }

  questionsComplete(data: any) {
    this.magnumResult = data;
    this.nextPage();
  }

  async bindPolicy() {
    this.loading = true

    try {
      var rsp = await this.api.bindPolicy(this.key);
      this.quickQuote.saveAnswer('PolicyNumber', rsp.policyNumber);

      this.logEvent('purchase', rsp);
      this.nextPage();
    } catch (err) {
      logError(`Error binding policy`, err);
      this.error = 'FullQuote.ErrorBindingPolicy';
    } finally {
      this.loading = false;
    }
  }

  private getAnswer(key: string) {
    return this.quote.answers.find(x => x.key === key)?.value;
  }

  private logEvent(event: string, rsp: BindPolicyResultModel) {
    let premium: string = this.quickQuote.answersObject['MagnumPremium'];
    let uid = this.quote?.result?.uid ?? this.quickQuote.answersObject["Uid"];

    let data: any = {
      page: this.analyticsService.getFullPath('full_quote'),
      leadType: 'fullQuote',
      policyNumber: rsp.policyNumber,
      amountPaid: premium,
      currency: 'GBP',
      firstName: this.getAnswer('FirstPolicyHolderFirstName'),
      lastName: this.getAnswer('FirstPolicyHolderSurName'),
      email: this.getAnswer('FirstPolicyHolderEmail'),
      uid: uid,
      quoteNo: this.quote.result?.quoteNo,
      leadkey: this.quote.parentKey ?? this.quote.key
    };

    if (event == "purchase") {
      data.isFirstPolicy = rsp.isFirstPolicy;
    }

    this.analyticsService.logEvent(event, data);
  }

  private checkAccess() {
    const hasCI = this.quote.answers.some(x => x.key.startsWith('PolicyType_') && x.value?.endsWith('-CI'));
    if (hasCI)
      return false;

    // ME 2024-08-09: Adina said that we can remove restriction on dual policies and allowing binding online for them
    // const singleOrDual = this.getAnswer('TypeOfPolicy_1');
    // if (singleOrDual === 'DUAL')
    //   return false;
    return true;
  }
}
